import { z } from 'zod'
import { HostState, HostRegion } from './host-state.js'
import { PublicSeriesRef } from './series-publication.js'

const Count = z.number().int().nonnegative()
const Generation = z.number().int().nonnegative()
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
/** What any public page may say about a non-draft show before its results are released. */
export const PublicShow = z
  .object({
    name: z.string(),
    slug: z.string(),
    startDate: DateOnly,
    endDate: DateOnly,
    location: z.string().nullable(),
  })
  .strict()
const ClassRef = z.object({ code: z.string(), name: z.string() }).strict()
const DivisionRef = z.object({ code: z.string(), name: z.string() }).strict()
/** A null entrant is a withheld or unrecorded name, never an empty string. */
const PublicPlacement = z
  .object({
    place: z.string(),
    exhibitId: z.string(),
    entrant: z.string().nullable(),
    club: z.string().nullable(),
    entryTitle: z.string().nullable(),
  })
  .strict()
const PublicWinner = z
  .object({
    exhibitId: z.string().nullable(),
    entrant: z.string().nullable(),
    club: z.string().nullable(),
    entryTitle: z.string().nullable(),
    class: ClassRef.nullable(),
    division: DivisionRef.nullable(),
  })
  .strict()
const PublicClass = z
  .object({
    code: z.string(),
    name: z.string(),
    categoryCode: z.string().nullable(),
    /** Shared model-class key, present only for a validated model link. */
    modelClassKey: z.string().nullable(),
    entries: Count,
    noAwards: z.boolean(),
    placements: z.array(PublicPlacement),
  })
  .strict()
/** Deliberately excludes internal IDs, contact details and staff-only analytics. */
export const ShowResultsReleasePayloadV1 = z
  .object({
    schemaVersion: z.literal(1),
    show: PublicShow,
    totals: z
      .object({
        entries: Count,
        entrants: Count,
        clubs: Count.nullable(),
        classesOffered: Count,
        classesWithEntries: Count,
      })
      .strict(),
    exhibitsPerEntrant: z.array(
      z.object({ label: z.string(), entrants: Count, entries: Count }).strict()
    ),
    divisions: z.array(
      z.object({ code: z.string(), name: z.string(), classes: z.array(PublicClass) }).strict()
    ),
    awards: z
      .object({
        bestInShow: PublicWinner.nullable(),
        grandChampion: PublicWinner.nullable(),
        reserveGrandChampions: z.array(PublicWinner),
        /** Null when the show does not award best in division. */
        bestInDivision: z
          .array(z.object({ division: DivisionRef, winner: PublicWinner }).strict())
          .nullable(),
        /** Null when the show does not give the Hero Award or no winner is selected. */
        heroAward: PublicWinner.nullable(),
      })
      .strict(),
  })
  .strict()
export const ShowResultsReleasePayloadV2 = ShowResultsReleasePayloadV1.extend({
  schemaVersion: z.literal(2),
  show: PublicShow.extend({ hostState: HostState.nullable() }).strict(),
}).strict()
export const ShowResultsReleasePayloadV3 = ShowResultsReleasePayloadV2.extend({
  schemaVersion: z.literal(3),
  /**
   * Null when clubs are not tracked; empty when no club is represented.
   * A total is negative when the show's configured placement values make it so.
   */
  clubStandings: z
    .array(z.object({ clubName: z.string(), points: z.number() }).strict())
    .nullable(),
}).strict()
const ChampionshipCounts = z
  .object({
    entrants: Count,
    entries: Count,
    ribbons: Count,
    firstPlaces: Count,
  })
  .strict()
export const ChampionshipStatistics = z
  .object({
    series: PublicSeriesRef,
    states: z.array(ChampionshipCounts.extend({ state: HostState }).strict()),
    unlocated: ChampionshipCounts,
    regions: z.array(
      z
        .object({
          region: HostRegion.nullable(),
          light: Count.nullable(),
          amber: Count.nullable(),
          dark: Count.nullable(),
        })
        .strict()
    ),
  })
  .strict()
export type ChampionshipStatisticsOutput = z.infer<typeof ChampionshipStatistics>
/** Null unless the show's series is a championship when the release is reviewed. */
export const ShowResultsReleasePayloadV4 = ShowResultsReleasePayloadV3.extend({
  schemaVersion: z.literal(4),
  championship: ChampionshipStatistics.nullable(),
}).strict()
/** Distinct entrants counted once per model class and show; pairs are unordered. */
export const ModelClassCoEntries = z.strictObject({
  classes: z.array(z.strictObject({ modelClassKey: z.string(), entrants: Count })),
  pairs: z.array(
    z.strictObject({ classKey: z.string(), alsoClassKey: z.string(), entrants: Count })
  ),
})
const PayloadV5Fields = ShowResultsReleasePayloadV4.extend({
  schemaVersion: z.literal(5),
  coEntries: ModelClassCoEntries,
}).strict()
function checkCoEntries(
  payload: Pick<z.infer<typeof PayloadV5Fields>, 'divisions' | 'totals' | 'coEntries'>,
  ctx: z.RefinementCtx
) {
  const offered = new Map<string, number>()
  for (const division of payload.divisions)
    for (const cls of division.classes)
      if (cls.modelClassKey)
        offered.set(cls.modelClassKey, (offered.get(cls.modelClassKey) ?? 0) + cls.entries)
  const counts = new Map<string, number>()
  for (const row of payload.coEntries.classes) {
    if (
      counts.has(row.modelClassKey) ||
      !offered.has(row.modelClassKey) ||
      row.entrants > Math.min(offered.get(row.modelClassKey) ?? 0, payload.totals.entrants)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['coEntries', 'classes'],
        message: 'Invalid model-class entrant count.',
      })
    counts.set(row.modelClassKey, row.entrants)
  }
  if (counts.size !== offered.size)
    ctx.addIssue({
      code: 'custom',
      path: ['coEntries', 'classes'],
      message: 'Every offered model class needs an entrant count.',
    })
  const seen = new Set<string>()
  for (const pair of payload.coEntries.pairs) {
    const key = JSON.stringify([pair.classKey, pair.alsoClassKey])
    if (
      pair.classKey >= pair.alsoClassKey ||
      seen.has(key) ||
      pair.entrants === 0 ||
      pair.entrants > Math.min(counts.get(pair.classKey) ?? 0, counts.get(pair.alsoClassKey) ?? 0)
    )
      ctx.addIssue({
        code: 'custom',
        path: ['coEntries', 'pairs'],
        message: 'Invalid or duplicate co-entry pair.',
      })
    seen.add(key)
  }
}
export const ShowResultsReleasePayloadV5 = PayloadV5Fields.superRefine(checkCoEntries)
const ClubAwardStanding = z.strictObject({
  clubName: z.string(),
  entries: Count,
  points: z.number(),
})
/** Equal totals share a place, so either list can name several clubs. */
const PublicClubAward = z.strictObject({
  winners: z.array(ClubAwardStanding).min(1),
  runnersUp: z.array(ClubAwardStanding),
})
export const ShowResultsReleasePayloadV6 = PayloadV5Fields.extend({
  schemaVersion: z.literal(6),
  /** Each is null when the show does not give the award or no club has earned it. */
  clubAwards: z.strictObject({
    clubSpirit: PublicClubAward.nullable(),
    clubChampion: PublicClubAward.nullable(),
  }),
})
  .strict()
  .superRefine(checkCoEntries)
/** Immutable version 1 to 5 releases remain readable beside new version 6 releases. */
export const ShowResultsReleasePayload = z.discriminatedUnion('schemaVersion', [
  ShowResultsReleasePayloadV1,
  ShowResultsReleasePayloadV2,
  ShowResultsReleasePayloadV3,
  ShowResultsReleasePayloadV4,
  ShowResultsReleasePayloadV5,
  ShowResultsReleasePayloadV6,
])
export type ShowResultsReleasePayloadOutput = z.infer<typeof ShowResultsReleasePayload>

export const ReviewedEntrantStanding = z.strictObject({
  entrant: z.string().nullable(),
  points: z.number(),
  entries: Count,
  placements: z.record(z.enum(['1st', '2nd', '3rd', '4th', '5th', '6th']), Count).optional(),
})
export const ShowResultsReleasePreviewResponse = z
  .object({
    token: z.string(),
    expiresAt: z.iso.datetime(),
    generation: Generation,
    payload: ShowResultsReleasePayload,
    entrantStandings: z.array(ReviewedEntrantStanding).optional(),
    uniqueEntrants: Count.optional(),
  })
  .strict()
export const ReleaseShowResultsRequest = z.object({ token: z.string().min(1).max(200) }).strict()
export const WithdrawShowResultsRequest = z.object({ expectedGeneration: Generation }).strict()
export const ShowResultsReleaseReceipt = z
  .object({
    releaseId: z.guid().nullable(),
    generation: Generation,
    releasedAt: z.iso.datetime().nullable(),
  })
  .strict()
export const ShowResultsReleaseStatus = z
  .object({
    generation: Generation,
    activeRelease: z
      .object({ releaseId: z.guid(), releasedAt: z.iso.datetime() })
      .strict()
      .nullable(),
    finalized: z.boolean(),
    canManage: z.boolean(),
    publicSlug: z.string(),
    /** Whether a fresh review would differ from what is public; null when nothing is released. */
    differsFromLive: z.boolean().nullable(),
  })
  .strict()
/**
 * `series` sits beside the results, never inside the immutable release payload: it is the
 * show's series as last published, or null when that series has no published edition.
 */
export const PublicShowResultsResponse = z.discriminatedUnion('released', [
  z
    .object({ released: z.literal(false), show: PublicShow, series: PublicSeriesRef.nullable() })
    .strict(),
  z
    .object({
      released: z.literal(true),
      releasedAt: z.iso.datetime(),
      results: ShowResultsReleasePayload,
      series: PublicSeriesRef.nullable(),
    })
    .strict(),
])
/** Editions of a published series whose show results are released, newest first. */
export const PublicSeriesResultsResponse = z
  .object({
    series: PublicSeriesRef,
    editions: z.array(
      z
        .object({
          year: z.number().int().min(1000).max(9999),
          /** The show's current public address, not the one kept in its release snapshot. */
          showSlug: z.string(),
          bestInShow: PublicWinner.nullable(),
        })
        .strict()
    ),
  })
  .strict()
export type ShowResultsReleasePreviewOutput = z.infer<typeof ShowResultsReleasePreviewResponse>
export type ShowResultsReleaseReceiptOutput = z.infer<typeof ShowResultsReleaseReceipt>
export type ShowResultsReleaseStatusOutput = z.infer<typeof ShowResultsReleaseStatus>
export type PublicShowResultsOutput = z.infer<typeof PublicShowResultsResponse>
export type PublicSeriesResultsOutput = z.infer<typeof PublicSeriesResultsResponse>
